import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  GitBranch,
  Search,
  RefreshCw,
  Loader,
  AlertCircle,
  CheckCircle,
  XCircle,
  ExternalLink,
  ArrowRight,
  Layers,
  Eye,
  Sparkles,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Link2,
  FileText,
  Network,
  FolderTree,
  Send,
  Clock,
  History,
  Zap,
  Home,
  Globe,
  Radar,
  Settings,
  Tag,
  Play,
  Save,
  Edit3,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import api from "../api/client";

function StatCard({ label, value, sub, icon: Icon, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={16} className={color} />}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

const TABS = [
  { key: "smart", label: "Smart Links", icon: Link2 },
  { key: "taxonomy", label: "Taxonomy", icon: Tag },
  { key: "crosssite", label: "Cross-Site", icon: Globe },
  { key: "orphans", label: "Orphan Pages", icon: AlertCircle },
  { key: "suggestions", label: "Suggestions", icon: Sparkles },
  { key: "autolinks", label: "Auto Links", icon: Zap },
  { key: "indexing", label: "Indexing", icon: Radar },
  { key: "linkmap", label: "Link Map", icon: Network },
  { key: "silos", label: "Silos", icon: FolderTree },
  { key: "history", label: "Crawl History", icon: History },
];

const IMPACT_COLORS = {
  high: { bg: "bg-red-900/20", text: "text-red-400" },
  medium: { bg: "bg-amber-900/20", text: "text-amber-400" },
  low: { bg: "bg-blue-900/20", text: "text-blue-400" },
};

/* ======== TAB: ORPHAN PAGES ======== */
function OrphansTab({ siteId }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [summary, setSummary] = useState(null);

  const fetchData = () => {
    Promise.all([
      api.get(`/websites/${siteId}/internal-links/pages/?orphan=true`),
      api.get(`/websites/${siteId}/internal-links/summary/`),
    ])
      .then(([pagesRes, summaryRes]) => {
        setPages(pagesRes.data.results || pagesRes.data);
        setSummary(summaryRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    fetchData();
  }, [siteId]);

  const handleCrawl = () => {
    setCrawling(true);
    api.post(`/websites/${siteId}/internal-links/crawl/`)
      .then(() => {
        // Poll for completion — also check crawl-sessions for status
        let pollCount = 0;
        const poll = setInterval(() => {
          pollCount++;
          Promise.all([
            api.get(`/websites/${siteId}/internal-links/summary/`),
            api.get(`/websites/${siteId}/internal-links/crawl-sessions/`),
          ]).then(([summaryRes, sessionsRes]) => {
            const sessions = sessionsRes.data.results || sessionsRes.data;
            const latest = sessions[0];
            // Stop polling when crawl completed or pages found
            if (summaryRes.data.total_pages > 0 || (latest && latest.status !== "running") || pollCount >= 30) {
              clearInterval(poll);
              setCrawling(false);
              fetchData();
            }
          }).catch(() => {});
        }, 5000);
        setTimeout(() => { clearInterval(poll); setCrawling(false); fetchData(); }, 180000);
      })
      .catch((err) => {
        setCrawling(false);
        alert(err.response?.data?.error || err.response?.data?.detail || "Failed to start crawl");
      });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Pages" value={summary.total_pages} icon={FileText} />
          <StatCard label="Orphan Pages" value={summary.orphan_pages} icon={AlertCircle} color="text-red-400" />
          <StatCard label="Content Links" value={summary.content_links} icon={Link2} color="text-blue-400" />
          <StatCard label="Avg Inbound" value={summary.avg_inbound} sub="links per page" icon={ArrowRight} color="text-green-400" />
        </div>
      )}

      {/* Crawl button */}
      <div className="flex justify-end">
        <button onClick={handleCrawl} disabled={crawling}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
          {crawling ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {crawling ? "Crawling..." : "Crawl Site"}
        </button>
      </div>

      {/* Orphan pages list */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-50 text-left">
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Page</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Title</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Words</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Inbound</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Outbound</th>
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                  <AlertCircle size={32} className="mx-auto mb-3 text-gray-600" />
                  <p>No orphan pages found.</p>
                  <p className="text-xs mt-1">{summary?.total_pages ? "All pages have inbound content links." : "Click \"Crawl Site\" to analyze your internal link structure."}</p>
                </td></tr>
              ) : pages.map((p) => (
                <tr key={p.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                  <td className="px-4 py-3">
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-copper hover:underline text-xs truncate max-w-[300px] block" title={p.url}>
                      {p.url.replace(/https?:\/\/[^/]+/, "")}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-gray-300 truncate max-w-[200px]">{p.title || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{p.word_count.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-xs ${p.inbound_internal_links === 0 ? "text-red-400" : "text-green-400"}`}>
                      {p.inbound_internal_links}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{p.outbound_internal_links}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ======== TAB: SUGGESTIONS ======== */
function SuggestionsTab({ siteId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("suggested");
  const [pushing, setPushing] = useState(null);
  const [bulkPushing, setBulkPushing] = useState(false);

  const fetchSuggestions = () => {
    api.get(`/websites/${siteId}/internal-links/suggestions/?status=${statusFilter}`)
      .then((res) => setSuggestions(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    fetchSuggestions();
  }, [siteId, statusFilter]);

  const [generateMsg, setGenerateMsg] = useState(null);

  const handleGenerate = () => {
    setGenerating(true);
    setGenerateMsg(null);
    const startCount = suggestions.length;
    let pollCount = 0;
    api.post(`/websites/${siteId}/internal-links/generate/`)
      .then(() => {
        // Poll every 5s until new suggestions appear or task completes
        const poll = setInterval(() => {
          pollCount++;
          api.get(`/websites/${siteId}/internal-links/suggestions/?status=suggested`)
            .then((res) => {
              const results = res.data.results || res.data;
              if (results.length > 0 && results.length !== startCount) {
                clearInterval(poll);
                setGenerating(false);
                setSuggestions(results);
              } else if (pollCount >= 6) {
                // After 30s, if still no new suggestions, task likely completed with 0 results
                clearInterval(poll);
                setGenerating(false);
                setSuggestions(results);
                if (results.length === 0) {
                  setGenerateMsg("No new link suggestions found. Try crawling the site first to build the link graph.");
                }
              }
            })
            .catch(() => {});
        }, 5000);
        // Hard timeout at 2 minutes
        setTimeout(() => {
          clearInterval(poll);
          setGenerating(false);
          fetchSuggestions();
        }, 120000);
      })
      .catch((err) => {
        setGenerating(false);
        alert(err.response?.data?.error || err.response?.data?.detail || "Failed to generate suggestions");
      });
  };

  const handleApprove = (id) => {
    api.post(`/websites/${siteId}/internal-links/suggestions/${id}/approve/`)
      .then(() => {
        setSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, status: "approved" } : s));
      });
  };

  const handleDismiss = (id) => {
    api.post(`/websites/${siteId}/internal-links/suggestions/${id}/dismiss/`)
      .then(() => {
        setSuggestions((prev) => prev.filter((s) => s.id !== id));
      });
  };

  const handlePush = (id) => {
    setPushing(id);
    api.post(`/websites/${siteId}/internal-links/suggestions/${id}/push/`)
      .then(() => {
        setSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, status: "pushed" } : s));
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.response?.data?.detail || "Push failed";
        alert(msg);
      })
      .finally(() => setPushing(null));
  };

  const handleBulkApprove = () => {
    const ids = suggestions.filter((s) => s.status === "suggested").map((s) => s.id);
    if (ids.length === 0) return;
    api.post(`/websites/${siteId}/internal-links/suggestions/bulk-approve/`, { ids })
      .then(() => {
        setSuggestions((prev) => prev.map((s) => ids.includes(s.id) ? { ...s, status: "approved" } : s));
      });
  };

  const handleBulkPush = () => {
    setBulkPushing(true);
    api.post(`/websites/${siteId}/internal-links/suggestions/bulk-push/`)
      .then((res) => {
        const data = res.data;
        fetchSuggestions();
        if (data.failed > 0) {
          const errorDetails = (data.errors || []).map((e) => e.error).join("\n");
          alert(`Pushed ${data.pushed} links. ${data.failed} failed.\n\n${errorDetails}`);
        } else {
          alert(`Successfully pushed ${data.pushed} links.`);
        }
      })
      .catch((err) => {
        alert(err.response?.data?.error || err.response?.data?.detail || "Bulk push failed");
      })
      .finally(() => setBulkPushing(false));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading suggestions...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[
            { key: "suggested", label: "Pending" },
            { key: "approved", label: "Approved" },
            { key: "pushed", label: "Pushed" },
            { key: "dismissed", label: "Dismissed" },
          ].map((f) => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {statusFilter === "suggested" && suggestions.length > 0 && (
            <button onClick={handleBulkApprove}
              className="text-xs px-3 py-1.5 border border-green-800 text-green-400 rounded-lg hover:bg-green-900/30 transition-colors">
              Approve All ({suggestions.length})
            </button>
          )}
          {statusFilter === "approved" && suggestions.length > 0 && (
            <button onClick={handleBulkPush} disabled={bulkPushing}
              className="flex items-center gap-1 text-xs px-3 py-1.5 border border-copper text-copper rounded-lg hover:bg-copper/10 transition-colors disabled:opacity-50">
              {bulkPushing ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
              {bulkPushing ? "Pushing..." : `Push All Approved (${suggestions.length})`}
            </button>
          )}
          <button onClick={handleGenerate} disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
            {generating ? <Loader size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Generating..." : "Generate Suggestions"}
          </button>
        </div>
      </div>

      {/* Generate result message */}
      {generateMsg && (
        <div className="bg-amber-900/10 border border-amber-800/30 rounded-xl p-4 text-sm text-amber-400 flex items-center gap-3">
          <AlertCircle size={18} className="shrink-0" />
          {generateMsg}
        </div>
      )}

      {/* Suggestions list */}
      <div className="space-y-3">
        {suggestions.length === 0 ? (
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
            <Sparkles size={32} className="mx-auto mb-3 text-gray-600" />
            <p>No {statusFilter} suggestions.</p>
            <p className="text-xs mt-1">Click "Generate Suggestions" to get AI-powered internal link recommendations.</p>
          </div>
        ) : suggestions.map((s) => (
          <div key={s.id} className="bg-dark-300 rounded-xl border border-dark-50 p-4">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                {/* Source → Target */}
                <div className="flex items-center gap-2 text-sm mb-2">
                  <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-copper truncate max-w-[250px]" title={s.source_url}>
                    {s.source_title || s.source_url.replace(/https?:\/\/[^/]+/, "")}
                  </a>
                  <ArrowRight size={14} className="text-copper shrink-0" />
                  <a href={s.target_url} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-copper truncate max-w-[250px]" title={s.target_url}>
                    {s.target_title || s.target_url.replace(/https?:\/\/[^/]+/, "")}
                  </a>
                </div>
                {/* Anchor text + Impact */}
                <div className="flex items-center gap-2 text-xs mb-2">
                  <span className="text-gray-500">Anchor:</span>
                  <span className="text-copper font-medium">"{s.anchor_text}"</span>
                  {s.impact_score && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${(IMPACT_COLORS[s.impact_score] || IMPACT_COLORS.medium).bg} ${(IMPACT_COLORS[s.impact_score] || IMPACT_COLORS.medium).text}`}>
                      {s.impact_score}
                    </span>
                  )}
                </div>
                {/* Context paragraph */}
                {s.context_paragraph && (
                  <div className="text-xs text-gray-500 bg-dark/50 rounded p-2 mb-2 border-l-2 border-copper/30">
                    "{s.context_paragraph.substring(0, 200)}{s.context_paragraph.length > 200 ? "..." : ""}"
                  </div>
                )}
                {/* AI reasoning */}
                {s.ai_reasoning && (
                  <p className="text-xs text-gray-500 italic">{s.ai_reasoning}</p>
                )}
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                {s.status === "suggested" && (
                  <>
                    <button onClick={() => handleApprove(s.id)}
                      className="p-1.5 text-green-400 hover:bg-green-900/30 rounded transition-colors" title="Approve">
                      <CheckCircle size={16} />
                    </button>
                    <button onClick={() => handleDismiss(s.id)}
                      className="p-1.5 text-gray-500 hover:bg-dark-50/50 rounded transition-colors" title="Dismiss">
                      <XCircle size={16} />
                    </button>
                  </>
                )}
                {s.status === "approved" && (
                  <button onClick={() => handlePush(s.id)} disabled={pushing === s.id}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors disabled:opacity-50">
                    {pushing === s.id ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                    Push
                  </button>
                )}
                {s.status === "pushed" && (
                  <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Pushed</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======== TAB: LINK MAP (Force Graph) ======== */
function LinkMapTab({ siteId }) {
  const [graphData, setGraphData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const [ForceGraph, setForceGraph] = useState(null);

  // Dynamically import react-force-graph-2d
  useEffect(() => {
    import("react-force-graph-2d").then((mod) => setForceGraph(() => mod.default));
  }, []);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/internal-links/authority-flow/`),
      api.get(`/websites/${siteId}/internal-links/summary/`),
    ])
      .then(([flowRes, summaryRes]) => {
        const data = flowRes.data;
        setGraphData({
          nodes: (data.nodes || []).map((n) => ({
            ...n,
            val: Math.max(n.authority_score / 10, 1),
          })),
          links: (data.edges || []).map((e) => ({
            source: e.source,
            target: e.target,
            label: e.anchor_text,
          })),
        });
        setSummary(summaryRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => {
    if (containerRef.current) {
      const { offsetWidth } = containerRef.current;
      setDimensions({ width: offsetWidth || 800, height: 500 });
    }
  }, [loading]);

  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const r = Math.max(node.val || 3, 2);
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = node.color || "#eab308";
    ctx.fill();
    if (globalScale > 1.5) {
      const label = (node.title || node.url || "").replace(/https?:\/\/[^/]+/, "").substring(0, 30);
      ctx.font = `${Math.max(3, 10 / globalScale)}px monospace`;
      ctx.fillStyle = "#9ca3af";
      ctx.textAlign = "center";
      ctx.fillText(label, node.x, node.y + r + 8 / globalScale);
    }
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading graph...</span></div>;
  }

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Pages" value={summary.total_pages} icon={FileText} />
          <StatCard label="Total Links" value={summary.total_links} icon={Link2} color="text-blue-400" />
          <StatCard label="Content Links" value={summary.content_links} icon={GitBranch} color="text-green-400" />
          <StatCard label="Nav Links" value={summary.nav_links} icon={Layers} color="text-gray-400" />
          <StatCard label="Silo Groups" value={summary.silo_groups} icon={FolderTree} color="text-purple-400" />
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> High Authority</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Moderate</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Weak/Orphan</span>
        <span className="ml-auto text-gray-600">Scroll to zoom · Drag to pan · Click nodes for details</span>
      </div>

      {/* Graph */}
      <div ref={containerRef} className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden relative" style={{ height: 500 }}>
        {ForceGraph && graphData && graphData.nodes.length > 0 ? (
          <ForceGraph
            graphData={graphData}
            width={dimensions.width}
            height={dimensions.height}
            backgroundColor="#0a0f1a"
            nodeCanvasObject={nodeCanvasObject}
            nodePointerAreaPaint={(node, color, ctx) => {
              const r = Math.max(node.val || 3, 2) + 2;
              ctx.beginPath();
              ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
              ctx.fillStyle = color;
              ctx.fill();
            }}
            linkColor={() => "rgba(184,115,51,0.15)"}
            linkWidth={0.5}
            linkDirectionalArrowLength={3}
            linkDirectionalArrowColor={() => "rgba(184,115,51,0.3)"}
            onNodeClick={(node) => setSelectedNode(node)}
            cooldownTicks={100}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Network size={32} className="mr-3 text-gray-600" />
            No graph data. Crawl your site first.
          </div>
        )}
      </div>

      {/* Selected node detail */}
      {selectedNode && (
        <div className="bg-dark-300 rounded-xl border border-copper/30 p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-medium text-white">{selectedNode.title || "Untitled"}</h3>
              <a href={selectedNode.url} target="_blank" rel="noopener noreferrer" className="text-xs text-copper hover:underline flex items-center gap-1 mt-1">
                {selectedNode.url} <ExternalLink size={10} />
              </a>
            </div>
            <button onClick={() => setSelectedNode(null)} className="text-gray-500 hover:text-white"><XCircle size={16} /></button>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-3 text-center">
            <div>
              <div className="text-lg font-bold font-mono text-copper">{selectedNode.authority_score}</div>
              <div className="text-[10px] text-gray-500 uppercase">Authority</div>
            </div>
            <div>
              <div className="text-lg font-bold font-mono">{selectedNode.inbound}</div>
              <div className="text-[10px] text-gray-500 uppercase">Inbound</div>
            </div>
            <div>
              <div className="text-lg font-bold font-mono">{selectedNode.outbound}</div>
              <div className="text-[10px] text-gray-500 uppercase">Outbound</div>
            </div>
          </div>
          {selectedNode.is_orphan && (
            <div className="mt-2 text-xs text-red-400 flex items-center gap-1"><AlertCircle size={12} /> Orphan page — no inbound content links</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ======== TAB: CRAWL HISTORY ======== */
function CrawlHistoryTab({ siteId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/internal-links/crawl-sessions/`)
      .then((res) => setSessions(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /></div>;

  const STATUS_COLORS = {
    running: "text-blue-400 bg-blue-900/20",
    completed: "text-green-400 bg-green-900/20",
    failed: "text-red-400 bg-red-900/20",
  };

  return (
    <div className="space-y-6">
      <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-50 text-left">
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Date</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Status</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Pages</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Orphans</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Suggestions</th>
                <th className="px-4 py-3 text-xs text-gray-500 font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                  <History size={32} className="mx-auto mb-3 text-gray-600" />
                  <p>No crawl history yet.</p>
                </td></tr>
              ) : sessions.map((s) => {
                const duration = s.completed_at && s.started_at
                  ? Math.round((new Date(s.completed_at) - new Date(s.started_at)) / 1000)
                  : null;
                return (
                  <tr key={s.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                    <td className="px-4 py-3 text-gray-300 text-xs font-mono">{new Date(s.started_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded capitalize ${STATUS_COLORS[s.status] || ""}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{s.pages_crawled}</td>
                    <td className="px-4 py-3 font-mono text-xs">{s.orphans_found}</td>
                    <td className="px-4 py-3 font-mono text-xs">{s.suggestions_generated}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{duration ? `${duration}s` : s.status === "running" ? "..." : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ======== TAB: SILOS ======== */
function SilosTab({ siteId }) {
  const [silos, setSilos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [expandedSilo, setExpandedSilo] = useState(null);
  const [siloPages, setSiloPages] = useState({});

  const fetchSilos = () => {
    api.get(`/websites/${siteId}/internal-links/silos/`)
      .then((res) => setSilos(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    fetchSilos();
  }, [siteId]);

  const handleCreate = () => {
    if (!newName.trim()) return;
    setCreating(true);
    api.post(`/websites/${siteId}/internal-links/silos/`, { name: newName, description: newDesc })
      .then((res) => {
        setSilos((prev) => [...prev, res.data]);
        setNewName("");
        setNewDesc("");
        setShowCreate(false);
      })
      .finally(() => setCreating(false));
  };

  const handleDelete = (id) => {
    api.delete(`/websites/${siteId}/internal-links/silos/${id}/`).then(() => {
      setSilos((prev) => prev.filter((s) => s.id !== id));
    });
  };

  const toggleExpand = (id) => {
    if (expandedSilo === id) {
      setExpandedSilo(null);
      return;
    }
    setExpandedSilo(id);
    // Fetch pages for this silo if not cached
    if (!siloPages[id]) {
      api.get(`/websites/${siteId}/internal-links/silos/${id}/`)
        .then((res) => {
          setSiloPages((prev) => ({ ...prev, [id]: res.data.page_ids || [] }));
        });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading silos...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Organize your pages into content silos for better topical authority.
        </p>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 transition-colors">
          <Plus size={16} /> New Silo
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-dark-300 rounded-xl border border-copper/30 p-4 space-y-3">
          <input type="text" placeholder="Silo name (e.g., Deck Building Services)" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          <input type="text" placeholder="Description (optional)" value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating || !newName.trim()}
              className="px-4 py-1.5 bg-copper text-dark rounded-lg text-sm font-medium disabled:opacity-50">
              {creating ? "Creating..." : "Create"}
            </button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-1.5 text-gray-400 text-sm hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {/* Silos list */}
      {silos.length === 0 ? (
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
          <FolderTree size={32} className="mx-auto mb-3 text-gray-600" />
          <p>No silos created yet.</p>
          <p className="text-xs mt-1">Create content silos to organize related pages together.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {silos.map((silo) => (
            <div key={silo.id} className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-dark-50/30 transition-colors"
                onClick={() => toggleExpand(silo.id)}
              >
                <div className="flex items-center gap-3">
                  <FolderTree size={16} className="text-copper" />
                  <div>
                    <h3 className="text-sm font-medium text-white">{silo.name}</h3>
                    {silo.description && <p className="text-xs text-gray-500">{silo.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 font-mono">{silo.page_count} pages</span>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(silo.id); }}
                    className="text-gray-600 hover:text-red-400 transition-colors p-1">
                    <Trash2 size={14} />
                  </button>
                  {expandedSilo === silo.id ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
                </div>
              </div>
              {expandedSilo === silo.id && (
                <div className="border-t border-dark-50 p-4">
                  {siloPages[silo.id]?.length > 0 ? (
                    <div className="space-y-1">
                      {siloPages[silo.id].map((pageId) => (
                        <div key={pageId} className="text-xs text-gray-400 font-mono">
                          Page #{pageId}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No pages assigned to this silo yet.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ======== TAB: AUTO LINKS (Bulk Internal Link Injection) ======== */
function AutoLinksTab({ siteId, selectedSite }) {
  const [serviceSlugs, setServiceSlugs] = useState(
    "interior-painting, exterior-painting, kitchen-remodeling, bathroom-remodeling, basement-finishing, home-renovation, cabinet-painting, pressure-washing, deck-staining"
  );
  const [citySlugs, setCitySlugs] = useState(
    "yonkers, white-plains, new-rochelle, scarsdale, mount-vernon, bronxville, rye, tarrytown, larchmont, mamaroneck, dobbs-ferry, hastings-on-hudson, irvington, ardsley, elmsford, tuckahoe, eastchester, pelham, pelham-manor, port-chester, rye-brook, harrison, ossining, croton-on-hudson, cortlandt, peekskill, yorktown, somers, katonah, bedford, chappaqua, pleasantville, briarcliff-manor, sleepy-hollow, nyack, nanuet, suffern, spring-valley, pearl-river, stony-point, haverstraw, west-haverstraw, congers, new-city, thiells"
  );
  const [urlPattern, setUrlPattern] = useState("/{service}-{city}-ny/");
  const [enableCrossService, setEnableCrossService] = useState(true);
  const [skipIfContains, setSkipIfContains] = useState("Cities We Serve");
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Homepage injection state
  const [homeServices, setHomeServices] = useState("interior-painting, kitchen-remodeling, bathroom-remodeling, cabinet-painting, exterior-painting, home-renovation");
  const [homeCities, setHomeCities] = useState("yonkers, white-plains, new-rochelle, scarsdale, mount-vernon, bronxville, rye, tarrytown, larchmont, mamaroneck");
  const [homeExecuting, setHomeExecuting] = useState(false);
  const [homeResult, setHomeResult] = useState(null);

  const parseList = (str) => str.split(",").map((s) => s.trim()).filter(Boolean);
  const slugToTitle = (slug) => slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  const buildPayload = (dryRun = false) => {
    const services = parseList(serviceSlugs);
    const cities = parseList(citySlugs);
    return {
      service_slugs: services,
      city_slugs: cities,
      service_names: services.map(slugToTitle),
      city_names: cities.map(slugToTitle),
      url_pattern: urlPattern,
      skip_if_contains: skipIfContains,
      enable_cross_service: enableCrossService,
      dry_run: dryRun,
    };
  };

  const handlePreview = () => {
    setPreviewing(true);
    setError(null);
    setPreview(null);
    api.post(`/websites/${siteId}/internal-links/auto/preview/`, buildPayload(true))
      .then((res) => setPreview(res.data))
      .catch((err) => setError(err.response?.data?.error || "Preview failed"))
      .finally(() => setPreviewing(false));
  };

  const handleExecute = () => {
    if (!window.confirm(`This will modify pages on ${selectedSite?.domain}. Continue?`)) return;
    setExecuting(true);
    setError(null);
    setResult(null);
    api.post(`/websites/${siteId}/internal-links/auto/execute/`, buildPayload(false))
      .then((res) => setResult(res.data))
      .catch((err) => setError(err.response?.data?.error || "Execution failed"))
      .finally(() => setExecuting(false));
  };

  const handleHomeInject = () => {
    if (!window.confirm(`This will modify the homepage on ${selectedSite?.domain}. Continue?`)) return;
    setHomeExecuting(true);
    setHomeResult(null);
    const services = parseList(homeServices);
    const cities = parseList(homeCities);
    api.post(`/websites/${siteId}/internal-links/homepage/inject/`, {
      page_id: "home",
      services,
      top_cities: cities,
      service_names: services.map(slugToTitle),
      city_names: cities.map(slugToTitle),
      url_pattern: urlPattern,
      skip_if_contains: "Service Areas",
    })
      .then((res) => setHomeResult(res.data))
      .catch((err) => setError(err.response?.data?.error || "Homepage injection failed"))
      .finally(() => setHomeExecuting(false));
  };

  return (
    <div className="space-y-6">
      {/* City Landing Page Links */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={18} className="text-copper" />
          <h3 className="text-white font-semibold">City Landing Page Internal Links</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Auto-inject "Cities We Serve" and cross-service link blocks into every city landing page.
          Creates a strong internal link network for better crawling and indexing.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Service Slugs (comma-separated)</label>
            <textarea value={serviceSlugs} onChange={(e) => setServiceSlugs(e.target.value)} rows={2}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">City Slugs (comma-separated)</label>
            <textarea value={citySlugs} onChange={(e) => setCitySlugs(e.target.value)} rows={3}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">URL Pattern</label>
              <input type="text" value={urlPattern} onChange={(e) => setUrlPattern(e.target.value)}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
              <p className="text-[10px] text-gray-600 mt-1">Use {"{service}"} and {"{city}"} placeholders</p>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Skip If Contains</label>
              <input type="text" value={skipIfContains} onChange={(e) => setSkipIfContains(e.target.value)}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={enableCrossService} onChange={(e) => setEnableCrossService(e.target.checked)}
              className="rounded border-dark-50 bg-dark text-copper focus:ring-copper" />
            Include cross-service links (other services in same city)
          </label>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={handlePreview} disabled={previewing}
              className="flex items-center gap-2 px-4 py-2 bg-dark-200 text-gray-300 border border-dark-50 rounded-lg hover:text-white hover:border-copper/30 transition-colors disabled:opacity-50 text-sm">
              {previewing ? <Loader size={14} className="animate-spin" /> : <Eye size={14} />}
              {previewing ? "Previewing..." : "Preview"}
            </button>
            <button onClick={handleExecute} disabled={executing}
              className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
              {executing ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
              {executing ? "Injecting..." : "Execute"}
            </button>
            <span className="text-xs text-gray-600 ml-2">
              {parseList(serviceSlugs).length} services x {parseList(citySlugs).length} cities = {parseList(serviceSlugs).length * parseList(citySlugs).length} pages
            </span>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/10 border border-red-800/30 rounded-xl p-4 text-sm text-red-400 flex items-center gap-3">
          <AlertCircle size={18} className="shrink-0" /> {error}
        </div>
      )}

      {/* Preview results */}
      {preview && (
        <div className="bg-dark-300 rounded-xl border border-copper/30 p-5">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Eye size={16} className="text-copper" /> Preview Results
          </h4>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <StatCard label="Would Update" value={preview.updated || 0} icon={CheckCircle} color="text-green-400" />
            <StatCard label="Would Skip" value={preview.skipped || 0} icon={XCircle} color="text-amber-400" />
            <StatCard label="Not Found" value={preview.not_found || 0} icon={AlertCircle} color="text-red-400" />
          </div>
          {(preview.details || []).length > 0 && (
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-dark-50 text-left">
                  <th className="px-3 py-2 text-gray-500">Page</th>
                  <th className="px-3 py-2 text-gray-500">Status</th>
                </tr></thead>
                <tbody>
                  {preview.details.map((d, i) => (
                    <tr key={i} className="border-b border-dark-50/50">
                      <td className="px-3 py-2 text-gray-300 font-mono">{d.slug || d.title}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          d.status === "would_update" ? "bg-green-900/20 text-green-400" :
                          d.status === "skipped" ? "bg-amber-900/20 text-amber-400" :
                          "bg-red-900/20 text-red-400"
                        }`}>{d.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Execution results */}
      {result && (
        <div className="bg-dark-300 rounded-xl border border-green-800/30 p-5">
          <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
            <CheckCircle size={16} /> Injection Complete
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Updated" value={result.updated || 0} icon={CheckCircle} color="text-green-400" />
            <StatCard label="Skipped" value={result.skipped || 0} icon={XCircle} color="text-amber-400" />
            <StatCard label="Not Found" value={result.not_found || 0} icon={AlertCircle} color="text-red-400" />
          </div>
        </div>
      )}

      {/* Homepage Service Areas */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Home size={18} className="text-copper" />
          <h3 className="text-white font-semibold">Homepage Service Area Links</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Add a "Service Areas" section to the homepage linking to top city pages. Homepage links carry the most authority.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Services to Feature (comma-separated slugs)</label>
            <textarea value={homeServices} onChange={(e) => setHomeServices(e.target.value)} rows={2}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Top Cities (comma-separated slugs)</label>
            <textarea value={homeCities} onChange={(e) => setHomeCities(e.target.value)} rows={2}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono" />
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleHomeInject} disabled={homeExecuting}
              className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
              {homeExecuting ? <Loader size={16} className="animate-spin" /> : <Home size={16} />}
              {homeExecuting ? "Injecting..." : "Inject Homepage Links"}
            </button>
          </div>
        </div>

        {homeResult && (
          <div className="mt-4 bg-green-900/10 border border-green-800/30 rounded-lg p-3 text-sm text-green-400 flex items-center gap-2">
            <CheckCircle size={14} />
            Homepage updated: {homeResult.services} services x {homeResult.cities} cities
          </div>
        )}
      </div>
    </div>
  );
}


/* ======== TAB: INDEXING TOOLS ======== */
function IndexingTab({ siteId, selectedSite }) {
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [urlsText, setUrlsText] = useState("");
  const [error, setError] = useState(null);

  const pingUrl = `/websites/${siteId}/indexing/ping/`;
  const submitUrl = `/websites/${siteId}/indexing/bulk-submit/`;

  const handlePing = () => {
    setPinging(true);
    setPingResult(null);
    setError(null);
    api.post(pingUrl)
      .then((res) => setPingResult(res.data))
      .catch((err) => setError(err.response?.data?.error || "Ping failed"))
      .finally(() => setPinging(false));
  };

  const handleBulkSubmit = () => {
    const urls = urlsText.split("\n").map((u) => u.trim()).filter(Boolean);
    if (urls.length === 0) return;
    setSubmitting(true);
    setSubmitResult(null);
    setError(null);
    api.post(submitUrl, { urls })
      .then((res) => setSubmitResult(res.data))
      .catch((err) => setError(err.response?.data?.error || "Submission failed"))
      .finally(() => setSubmitting(false));
  };

  const generateUrls = () => {
    const domain = selectedSite?.domain ? `https://${selectedSite.domain}` : "";
    if (!domain) return;
    const services = ["interior-painting", "exterior-painting", "kitchen-remodeling", "bathroom-remodeling", "basement-finishing", "home-renovation", "cabinet-painting", "pressure-washing", "deck-staining"];
    const topCities = ["yonkers", "white-plains", "new-rochelle", "scarsdale", "mount-vernon", "bronxville", "rye", "tarrytown", "larchmont", "mamaroneck"];
    const urls = [];
    for (const svc of services) {
      for (const city of topCities) {
        urls.push(`${domain}/${svc}-${city}-ny/`);
      }
    }
    setUrlsText(urls.join("\n"));
  };

  return (
    <div className="space-y-6">
      {/* Notify Search Engines */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={18} className="text-copper" />
          <h3 className="text-white font-semibold">Notify Search Engines</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Submit your sitemap to Google (via Search Console API) and Bing (via IndexNow). This notifies search engines of new or changed pages.
        </p>

        <button onClick={handlePing} disabled={pinging}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
          {pinging ? <Loader size={16} className="animate-spin" /> : <Globe size={16} />}
          {pinging ? "Submitting..." : "Submit Sitemap"}
        </button>

        {pingResult && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className={`rounded-lg p-3 border ${pingResult.google ? "border-green-800/30 bg-green-900/10" : "border-red-800/30 bg-red-900/10"}`}>
              <div className="flex items-center gap-2">
                {pingResult.google ? <CheckCircle size={14} className="text-green-400" /> : <XCircle size={14} className="text-red-400" />}
                <span className={`text-sm font-medium ${pingResult.google ? "text-green-400" : "text-red-400"}`}>
                  Google {pingResult.google ? "OK" : "Failed"}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {pingResult.google_method && <span className="text-copper">{pingResult.google_method} — </span>}
                {pingResult.google_detail || pingResult.sitemap_url}
              </p>
            </div>
            <div className={`rounded-lg p-3 border ${pingResult.bing ? "border-green-800/30 bg-green-900/10" : "border-red-800/30 bg-red-900/10"}`}>
              <div className="flex items-center gap-2">
                {pingResult.bing ? <CheckCircle size={14} className="text-green-400" /> : <XCircle size={14} className="text-red-400" />}
                <span className={`text-sm font-medium ${pingResult.bing ? "text-green-400" : "text-red-400"}`}>
                  Bing {pingResult.bing ? "OK" : "Failed"}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {pingResult.bing_method && <span className="text-copper">{pingResult.bing_method} — </span>}
                {pingResult.bing_detail || ""}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bulk URL Submission */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Radar size={18} className="text-copper" />
          <h3 className="text-white font-semibold">Bulk URL Submission</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Submit URLs to Google Indexing API for faster crawling. Falls back to IndexNow if Indexing API is not configured.
          Max 200 URLs per request.
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={generateUrls}
              className="flex items-center gap-1 text-xs px-3 py-1.5 bg-dark-200 text-gray-400 border border-dark-50 rounded-lg hover:text-white transition-colors">
              <Zap size={12} /> Generate City Page URLs
            </button>
            <span className="text-[10px] text-gray-600">Auto-generates URLs for all service+city combinations</span>
          </div>

          <textarea value={urlsText} onChange={(e) => setUrlsText(e.target.value)} rows={8}
            placeholder="Enter URLs, one per line..."
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none font-mono text-xs" />

          <div className="flex items-center gap-2">
            <button onClick={handleBulkSubmit} disabled={submitting || !urlsText.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors">
              {submitting ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? "Submitting..." : `Submit ${urlsText.split("\n").filter(Boolean).length} URLs`}
            </button>
          </div>
        </div>

        {submitResult && (
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <StatCard label="Submitted" value={submitResult.submitted || 0} icon={CheckCircle} color="text-green-400" />
              <StatCard label="Failed" value={submitResult.failed || 0} icon={XCircle} color="text-red-400" />
            </div>
            {(submitResult.details || []).filter((d) => d.status === "failed").length > 0 && (
              <div className="max-h-40 overflow-y-auto bg-dark/50 rounded-lg p-2">
                {submitResult.details.filter((d) => d.status === "failed").map((d, i) => (
                  <div key={i} className="text-[10px] text-red-400 font-mono py-0.5">{d.url}: {d.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-900/10 border border-red-800/30 rounded-xl p-4 text-sm text-red-400 flex items-center gap-3">
          <AlertCircle size={18} className="shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}


/* ======== SMART LINKS TAB (v3.4.0) ======== */
function SmartLinksTab({ siteId }) {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [injecting, setInjecting] = useState(false);
  const [injectResult, setInjectResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/bridge/internal-links/stats/`),
      api.get(`/websites/${siteId}/bridge/internal-links/settings/`),
    ])
      .then(([s, st]) => {
        setStats(s.data);
        setSettings(st.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const saveSettings = () => {
    setSaving(true);
    setMsg("");
    api.post(`/websites/${siteId}/bridge/internal-links/settings/`, settings)
      .then((res) => { setSettings(res.data); setMsg("Settings saved"); })
      .catch(() => setMsg("Save failed"))
      .finally(() => setSaving(false));
  };

  const runInject = () => {
    setInjecting(true);
    setInjectResult(null);
    api.post(`/websites/${siteId}/bridge/internal-links/inject/`, { page_ids: "all", dry_run: dryRun })
      .then((res) => setInjectResult(res.data))
      .catch(() => setInjectResult({ error: "Injection failed" }))
      .finally(() => setInjecting(false));
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-500 py-8"><Loader size={18} className="animate-spin" /> Loading smart links data...</div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Pages" value={stats.total_pages} icon={FileText} />
          <StatCard label="Zero Links" value={stats.pages_zero_links} icon={AlertCircle} color="text-red-400" />
          <StatCard label="Overrides" value={stats.pages_with_override} icon={Edit3} />
          <StatCard label="Taxonomy" value={stats.taxonomy_entries} icon={Tag} />
          <StatCard label="Cross-Site" value={stats.crosssite_mappings} icon={Globe} />
        </div>
      )}

      {/* Settings */}
      {settings && (
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-5 space-y-4">
          <h3 className="text-lg font-semibold">Related Services Block Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Enabled</label>
              <button onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className="flex items-center gap-2 text-sm">
                {settings.enabled ? <ToggleRight size={22} className="text-green-400" /> : <ToggleLeft size={22} className="text-gray-500" />}
                {settings.enabled ? "On" : "Off"}
              </button>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Max Links</label>
              <input type="number" min={3} max={15} value={settings.max_links}
                onChange={(e) => setSettings({ ...settings, max_links: parseInt(e.target.value) || 7 })}
                className="w-20 bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Show On</label>
              <select value={settings.show_on} onChange={(e) => setSettings({ ...settings, show_on: e.target.value })}
                className="bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm">
                <option value="both">Pages & Posts</option>
                <option value="pages">Pages Only</option>
                <option value="posts">Posts Only</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Cross-Site Links</label>
              <button onClick={() => setSettings({ ...settings, crosssite: !settings.crosssite })}
                className="flex items-center gap-2 text-sm">
                {settings.crosssite ? <ToggleRight size={22} className="text-green-400" /> : <ToggleLeft size={22} className="text-gray-500" />}
                {settings.crosssite ? "Enabled" : "Disabled"}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={saveSettings} disabled={saving}
              className="flex items-center gap-2 bg-copper hover:bg-copper/80 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
            </button>
            {msg && <span className="text-sm text-green-400">{msg}</span>}
          </div>
        </div>
      )}

      {/* Content Injection */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-5 space-y-4">
        <h3 className="text-lg font-semibold">Content Injection</h3>
        <p className="text-sm text-gray-400">
          Inject internal links into pages that currently have zero internal links in their content body.
        </p>
        <div className="flex items-center gap-4">
          <button onClick={() => setDryRun(!dryRun)} className="flex items-center gap-2 text-sm">
            {dryRun ? <ToggleRight size={22} className="text-amber-400" /> : <ToggleLeft size={22} className="text-gray-500" />}
            Dry Run {dryRun ? "(Preview Only)" : "(Will Modify Content)"}
          </button>
          <button onClick={runInject} disabled={injecting}
            className="flex items-center gap-2 bg-copper hover:bg-copper/80 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {injecting ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
            {dryRun ? "Preview Injection" : "Execute Injection"}
          </button>
        </div>
        {injectResult && !injectResult.error && (
          <div className="space-y-3">
            <div className="flex gap-4 text-sm">
              <span className="text-gray-400">Total: <strong className="text-white">{injectResult.total}</strong></span>
              <span className="text-green-400">Fixed: <strong>{injectResult.fixed}</strong></span>
              <span className="text-gray-500">Skipped: <strong>{injectResult.skipped}</strong></span>
              {injectResult.dry_run && <span className="bg-amber-900/20 text-amber-400 px-2 py-0.5 rounded text-xs">DRY RUN</span>}
            </div>
            {injectResult.results?.length > 0 && (
              <div className="max-h-64 overflow-y-auto space-y-1">
                {injectResult.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-dark-50">
                    {r.status === "fixed" || r.status === "preview" ? (
                      <CheckCircle size={14} className="text-green-400 shrink-0" />
                    ) : (
                      <XCircle size={14} className="text-gray-600 shrink-0" />
                    )}
                    <span className="text-gray-300 truncate flex-1">{r.title}</span>
                    {r.injected && <span className="text-copper text-xs">+{r.injected} links</span>}
                    {r.reason && <span className="text-gray-600 text-xs">{r.reason}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {injectResult?.error && <div className="text-red-400 text-sm">{injectResult.error}</div>}
      </div>
    </div>
  );
}


/* ======== TAXONOMY TAB (v3.4.0) ======== */
function TaxonomyTab({ siteId }) {
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editRelated, setEditRelated] = useState("");

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/bridge/internal-links/taxonomy/`)
      .then((res) => setTaxonomy(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const saveTaxonomy = () => {
    setSaving(true);
    setMsg("");
    api.post(`/websites/${siteId}/bridge/internal-links/taxonomy/`, taxonomy)
      .then((res) => { setTaxonomy(res.data); setMsg("Taxonomy saved"); })
      .catch(() => setMsg("Save failed"))
      .finally(() => setSaving(false));
  };

  const startEdit = (groupKey, entryKey) => {
    const entry = taxonomy[groupKey][entryKey];
    setEditingEntry({ group: groupKey, key: entryKey });
    setEditLabel(entry.label);
    setEditRelated((entry.related || []).join(", "));
  };

  const saveEdit = () => {
    if (!editingEntry) return;
    const { group, key } = editingEntry;
    const updated = { ...taxonomy };
    updated[group] = { ...updated[group] };
    updated[group][key] = {
      ...updated[group][key],
      label: editLabel,
      related: editRelated.split(",").map((s) => s.trim()).filter(Boolean),
    };
    setTaxonomy(updated);
    setEditingEntry(null);
  };

  const deleteEntry = (groupKey, entryKey) => {
    const updated = { ...taxonomy };
    updated[groupKey] = { ...updated[groupKey] };
    delete updated[groupKey][entryKey];
    if (Object.keys(updated[groupKey]).length === 0) delete updated[groupKey];
    setTaxonomy(updated);
  };

  const slugToTitle = (s) => s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  if (loading) return <div className="flex items-center gap-2 text-gray-500 py-8"><Loader size={18} className="animate-spin" /> Loading taxonomy...</div>;
  if (!taxonomy) return <div className="text-gray-500 py-8">No taxonomy data available.</div>;

  const groups = Object.keys(taxonomy);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">Service taxonomy defines related-service links for the Related Services block.</p>
        <button onClick={saveTaxonomy} disabled={saving}
          className="flex items-center gap-2 bg-copper hover:bg-copper/80 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Save Taxonomy
        </button>
      </div>
      {msg && <div className="text-sm text-green-400">{msg}</div>}

      {groups.map((groupKey) => {
        const entries = Object.entries(taxonomy[groupKey]);
        const isExpanded = expandedGroup === groupKey;
        return (
          <div key={groupKey} className="bg-dark-300 rounded-xl border border-dark-50">
            <button onClick={() => setExpandedGroup(isExpanded ? null : groupKey)}
              className="w-full flex items-center justify-between px-5 py-3 text-left">
              <span className="font-medium">{slugToTitle(groupKey)} <span className="text-xs text-gray-500 ml-2">{entries.length} entries</span></span>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isExpanded && (
              <div className="border-t border-dark-50 divide-y divide-dark-50">
                {entries.map(([entryKey, entry]) => (
                  <div key={entryKey} className="px-5 py-3">
                    {editingEntry?.group === groupKey && editingEntry?.key === entryKey ? (
                      <div className="space-y-2">
                        <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                          className="w-full bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm" placeholder="Label" />
                        <input value={editRelated} onChange={(e) => setEditRelated(e.target.value)}
                          className="w-full bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm" placeholder="Related slugs (comma-separated)" />
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded">Save</button>
                          <button onClick={() => setEditingEntry(null)} className="text-xs bg-dark-100 text-gray-400 px-3 py-1 rounded">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-copper">{entryKey}</span>
                          <span className="text-gray-500 text-xs ml-2">{entry.label}</span>
                          {entry.related?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {entry.related.map((r) => (
                                <span key={r} className="bg-dark-100 text-gray-400 text-xs px-2 py-0.5 rounded">{r}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0 ml-3">
                          <button onClick={() => startEdit(groupKey, entryKey)} className="p-1 hover:text-copper"><Edit3 size={14} /></button>
                          <button onClick={() => deleteEntry(groupKey, entryKey)} className="p-1 hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ======== CROSS-SITE LINKS TAB (v3.4.0) ======== */
function CrossSiteTab({ siteId }) {
  const [mappings, setMappings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [editAnchor, setEditAnchor] = useState("");
  const [editContext, setEditContext] = useState("");
  const [newKey, setNewKey] = useState("");

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.get(`/websites/${siteId}/bridge/internal-links/crosssite/`)
      .then((res) => setMappings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const saveMappings = () => {
    setSaving(true);
    setMsg("");
    api.post(`/websites/${siteId}/bridge/internal-links/crosssite/`, mappings)
      .then((res) => { setMappings(res.data); setMsg("Cross-site links saved"); })
      .catch(() => setMsg("Save failed"))
      .finally(() => setSaving(false));
  };

  const startEdit = (key) => {
    const m = mappings[key];
    setEditingKey(key);
    setEditUrl(m.url);
    setEditAnchor(m.anchor);
    setEditContext(m.context || "");
  };

  const saveEdit = () => {
    if (!editingKey) return;
    setMappings({ ...mappings, [editingKey]: { url: editUrl, anchor: editAnchor, context: editContext } });
    setEditingKey(null);
  };

  const deleteMapping = (key) => {
    const updated = { ...mappings };
    delete updated[key];
    setMappings(updated);
  };

  const addMapping = () => {
    if (!newKey.trim()) return;
    const key = newKey.trim().toLowerCase().replace(/\s+/g, "-");
    setMappings({ ...mappings, [key]: { url: "", anchor: "", context: "" } });
    setNewKey("");
    setEditingKey(key);
    setEditUrl("");
    setEditAnchor("");
    setEditContext("");
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-500 py-8"><Loader size={18} className="animate-spin" /> Loading cross-site links...</div>;
  if (!mappings) return <div className="text-gray-500 py-8">No cross-site link data available.</div>;

  const entries = Object.entries(mappings);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">
          Cross-site links connect related services across your owned domains (uses <code className="text-copper">rel="noopener"</code>, not nofollow).
        </p>
        <button onClick={saveMappings} disabled={saving}
          className="flex items-center gap-2 bg-copper hover:bg-copper/80 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Save Mappings
        </button>
      </div>
      {msg && <div className="text-sm text-green-400">{msg}</div>}

      <div className="bg-dark-300 rounded-xl border border-dark-50 divide-y divide-dark-50">
        {entries.map(([key, m]) => (
          <div key={key} className="px-5 py-3">
            {editingKey === key ? (
              <div className="space-y-2">
                <div className="text-xs text-gray-500 mb-1">Slug pattern: <strong className="text-copper">{key}</strong></div>
                <input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="Target URL"
                  className="w-full bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm" />
                <input value={editAnchor} onChange={(e) => setEditAnchor(e.target.value)} placeholder="Anchor text"
                  className="w-full bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm" />
                <input value={editContext} onChange={(e) => setEditContext(e.target.value)} placeholder="Context (optional)"
                  className="w-full bg-dark-100 border border-dark-50 rounded px-3 py-1.5 text-sm" />
                <div className="flex gap-2">
                  <button onClick={saveEdit} className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded">Save</button>
                  <button onClick={() => setEditingKey(null)} className="text-xs bg-dark-100 text-gray-400 px-3 py-1 rounded">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-copper">{key}</span>
                    <ArrowRight size={14} className="text-gray-600 shrink-0" />
                    <span className="text-sm text-gray-300 truncate">{m.anchor}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">{m.url}</div>
                  {m.context && <div className="text-xs text-gray-600 mt-0.5">{m.context}</div>}
                </div>
                <div className="flex gap-1 shrink-0 ml-3">
                  <button onClick={() => startEdit(key)} className="p-1 hover:text-copper"><Edit3 size={14} /></button>
                  <button onClick={() => deleteMapping(key)} className="p-1 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add new mapping */}
      <div className="flex items-center gap-2">
        <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="New slug pattern (e.g. dental-hvac)"
          className="flex-1 bg-dark-300 border border-dark-50 rounded px-3 py-2 text-sm"
          onKeyDown={(e) => e.key === "Enter" && addMapping()} />
        <button onClick={addMapping} className="flex items-center gap-2 bg-dark-300 border border-dark-50 hover:border-copper text-sm px-4 py-2 rounded-lg">
          <Plus size={14} /> Add Mapping
        </button>
      </div>
    </div>
  );
}


/* ======== MAIN COMPONENT ======== */
export default function InternalLinkBuilder({ selectedSite }) {
  const [activeTab, setActiveTab] = useState("smart");
  const siteId = selectedSite?.id;

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to analyze internal links.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-mono">
          <span className="text-copper">Internal</span> Link Builder
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Find orphan pages, get AI link suggestions, and manage content silos
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-x-auto w-fit max-w-full">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"
              }`}>
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "smart" && <SmartLinksTab siteId={siteId} />}
      {activeTab === "taxonomy" && <TaxonomyTab siteId={siteId} />}
      {activeTab === "crosssite" && <CrossSiteTab siteId={siteId} />}
      {activeTab === "orphans" && <OrphansTab siteId={siteId} />}
      {activeTab === "suggestions" && <SuggestionsTab siteId={siteId} />}
      {activeTab === "autolinks" && <AutoLinksTab siteId={siteId} selectedSite={selectedSite} />}
      {activeTab === "indexing" && <IndexingTab siteId={siteId} selectedSite={selectedSite} />}
      {activeTab === "linkmap" && <LinkMapTab siteId={siteId} />}
      {activeTab === "silos" && <SilosTab siteId={siteId} />}
      {activeTab === "history" && <CrawlHistoryTab siteId={siteId} />}
    </div>
  );
}
